import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, Empty, ChannelBadge, CHANNEL_META, CHANNELS } from "@/components/ui-bits";
import { fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus } from "@phosphor-icons/react";

export default function Communications() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [logs, setLogs] = useState([]);
  const canEdit = ["super_admin", "client_admin", "manager"].includes(user.role);

  const load = () => {
    api.get("/templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/communications").then((r) => setLogs(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="communications-page">
      <PageHeader title="Communication Hub" subtitle="Templates & delivery logs" />

      <Tabs defaultValue="templates">
        <TabsList className="rounded-sm">
          <TabsTrigger value="templates" className="rounded-sm" data-testid="tab-templates">Templates</TabsTrigger>
          <TabsTrigger value="logs" className="rounded-sm" data-testid="tab-logs">Delivery Logs</TabsTrigger>
        </TabsList>

        <TabsContent value="templates" className="mt-4">
          {canEdit && <div className="mb-4"><TemplateDialog onDone={load} /></div>}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((t) => {
              const meta = CHANNEL_META[t.channel] || CHANNEL_META.sms;
              const Icon = meta.Icon;
              return (
                <div key={t.id} className="border border-border bg-card p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Icon size={18} weight="bold" className={meta.color} />
                      <span className="font-semibold text-sm">{t.name}</span>
                    </div>
                    <StatusBadge status={t.status} />
                  </div>
                  <p className="text-sm text-muted-foreground mt-3">{t.body}</p>
                  <div className="label-caps mt-3">{meta.label} • {t.type.replace(/_/g, " ")}</div>
                </div>
              );
            })}
          </div>
          {templates.length === 0 && <Empty text="No templates yet" />}
        </TabsContent>

        <TabsContent value="logs" className="mt-4">
          <div className="border border-border bg-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="label-caps border-b border-border bg-secondary/40">
                  <th className="text-left p-3">Time</th>
                  <th className="text-left p-3">Borrower</th>
                  <th className="text-left p-3">Channel</th>
                  <th className="text-left p-3">To</th>
                  <th className="text-left p-3">Message</th>
                  <th className="text-center p-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id} className="border-b border-border last:border-0 hover:bg-secondary/50">
                    <td className="p-3 mono text-xs text-muted-foreground">{fmtDateTime(l.created_at)}</td>
                    <td className="p-3 font-medium">{l.borrower_name}</td>
                    <td className="p-3"><ChannelBadge channel={l.channel} /></td>
                    <td className="p-3 mono text-xs">{l.to}</td>
                    <td className="p-3 text-muted-foreground max-w-xs truncate">{l.body}</td>
                    <td className="p-3 text-center"><StatusBadge status={l.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {logs.length === 0 && <Empty text="No communications sent" />}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function TemplateDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", channel: "whatsapp", type: "reminder", body: "", status: "active" });

  const submit = async () => {
    if (!f.name || !f.body) return toast.error("Name and body required");
    try {
      await api.post("/templates", f);
      toast.success("Template created");
      setOpen(false);
      setF({ name: "", channel: "whatsapp", type: "reminder", body: "", status: "active" });
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-sm gap-2" data-testid="add-template-button"><Plus size={16} weight="bold" /> New Template</Button>
      </DialogTrigger>
      <DialogContent className="rounded-sm">
        <DialogHeader>
          <DialogTitle>New Communication Template</DialogTitle>
          <DialogDescription>Create a reusable message template for borrower communications.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Name</Label><Input className="rounded-sm" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="tpl-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Channel</Label>
              <Select value={f.channel} onValueChange={(v) => setF({ ...f, channel: v })}>
                <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{CHANNELS.map((c) => <SelectItem key={c} value={c}>{CHANNEL_META[c].label}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-xs">Type</Label>
              <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
                <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{["reminder", "overdue", "payment_link", "settlement_offer", "settlement_approval"].map((c) => <SelectItem key={c} value={c} className="capitalize">{c.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div><Label className="text-xs">Body</Label><Textarea className="rounded-sm" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} placeholder="Use {name}, {amount}, {loan_id}…" data-testid="tpl-body" /></div>
        </div>
        <DialogFooter><Button onClick={submit} className="rounded-sm" data-testid="save-template-button">Create</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
